import Tts, { type SearchReplacePattern } from "#/feature/tts-playback/tts-plugin";

export type { SearchReplacePattern };

export async function getPatterns(): Promise<SearchReplacePattern[]> {
  try {
    const state = await Tts.getState();
    return state.searchReplacePatterns ?? [];
  } catch (error) {
    console.error("Failed to load search/replace patterns", error);
    return [];
  }
}

export async function savePatterns(patterns: SearchReplacePattern[]): Promise<void> {
  await Tts.setSearchReplacePatterns({ patterns });
}

export async function addPattern(pattern: SearchReplacePattern): Promise<void> {
  const patterns = await getPatterns();
  patterns.push(pattern);
  await savePatterns(patterns);
}

export async function updatePattern(
  id: string,
  updates: Partial<Omit<SearchReplacePattern, "id">>,
): Promise<void> {
  const patterns = await getPatterns();
  const index = patterns.findIndex((p) => p.id === id);
  if (index === -1) {
    throw new Error(`Pattern with id ${id} not found`);
  }
  const existing = patterns[index]!;
  patterns[index] = {
    id: existing.id,
    searchPattern: updates.searchPattern ?? existing.searchPattern,
    replaceValue: updates.replaceValue ?? existing.replaceValue,
    enabled: updates.enabled ?? existing.enabled,
    applyToUi: updates.applyToUi ?? existing.applyToUi,
    flags: updates.flags ?? existing.flags,
  };
  await savePatterns(patterns);
}

export async function deletePattern(id: string): Promise<void> {
  const patterns = await getPatterns();
  const filtered = patterns.filter((p) => p.id !== id);
  await savePatterns(filtered);
}

export async function applySearchReplace(text: string, forUi: boolean): Promise<string> {
  const patterns = await getPatterns();
  return applyPatterns(text, patterns, forUi);
}

export function normalizeCaptureGroups(replacement: string): string {
  // Convert unescaped \1, \2, ... to $1, $2, ... for regex replacement
  return replacement.replace(/(?<!\\)\\([0-9])/g, "$$$1");
}

export function applyPatterns(
  text: string,
  patterns: SearchReplacePattern[],
  forUi: boolean,
): string {
  let result = text;

  for (const pattern of patterns) {
    if (!pattern.enabled) {
      continue;
    }
    if (forUi && !pattern.applyToUi) {
      continue;
    }

    try {
      const flags = pattern.flags !== undefined ? pattern.flags : "gi";
      // Ensure 'g' is present so all occurrences are replaced
      const effectiveFlags = flags.includes("g") ? flags : `${flags}g`;
      const regex = new RegExp(pattern.searchPattern, effectiveFlags);
      const normalizedReplace = normalizeCaptureGroups(pattern.replaceValue);
      result = result.replace(regex, normalizedReplace);
    } catch (error) {
      // Invalid regex, skip this pattern
      console.error(`Invalid regex pattern: ${pattern.searchPattern}`, error);
    }
  }

  return result;
}

export function validateRegex(pattern: string): { valid: boolean; error?: string } {
  try {
    new RegExp(pattern);
    return { valid: true };
  } catch (error) {
    return { valid: false, error: (error as Error).message };
  }
}
